<script setup>
import CustomFieldValue from "@/components/customFields/CustomFieldValue.vue";
import {inject, onMounted, ref} from "vue";
import startupsService from "@/services/startups.service";
import ProfileTrlData from "@/views/profile/ProfileTrlData.vue";
import {getNoun} from "@/helpers/string";
import StartupPartnershipsModal from "@/views/profile/StartupPartnershipsModal.vue";

const trlFields = [-6, -7, -8, -9, -10];

const companyId = inject('companyId');
const companyName = inject('companyName');
const customFieldsFeedbacks = inject('customFieldsFeedbacks');
const setCustomFieldFeedback = inject('setCustomFieldFeedback');

const aiData = ref([]);
const allPatents = ref(null);
const partnershipsModal = ref(null);

onMounted(() => {
    getAiData();
})

async function getAiData() {
    const {data} = await startupsService.getAiData(companyId);

    let hasTrlFields = false;
    let trlFieldsIndex;

    for (let i in data) {
        if (trlFields.includes(data[i].id)) {
            if (!hasTrlFields) {
                aiData.value.push({
                    trl: true,
                    fields: []
                });

                hasTrlFields = true;
                trlFieldsIndex = aiData.value.length - 1;
            }
        } else {
            let firstSection = !aiData.value.length;
            let lastSection = hasTrlFields && aiData.value.length - 1 === trlFieldsIndex;

            if (firstSection || lastSection) {
                aiData.value.push({
                    trl: false,
                    firstSection: firstSection,
                    lastSection: lastSection,
                    fields: []
                });
            }
        }

        aiData.value[aiData.value.length - 1].fields.push(data[i]);
    }
}

async function loadAllPatents() {
    const {data} = await startupsService.getPatents(companyId);
    allPatents.value = data;
}

function goToPage(url) {
    window.open(url,'_blank');
}

</script>

<template>
    <div class="main-startup__tabs__item" v-if="aiData?.length">
        <header class="main-startup__tabs__header">
            <h4 class="heading4 heading4--tertiary">Company Details</h4>

            <span class="ai-generated mr-auto">AI Generated</span>
        </header>

        <div class="main-startup__tabs__item__inner">
            <table class="details-table details-ai-generated">
                <template v-for="(section, index) in aiData">
                    <ProfileTrlData
                        v-if="section.trl"
                        :key="index"
                        :fields="section.fields"
                    />

                    <tbody v-else :key="'main-' + index">
                    <tr v-for="field in section.fields" :key="field.id" :class="{'first-section': section.firstSection, 'last-section': section.lastSection}">
                        <td>
                            {{field.name}}
                        </td>

                        <td>
                            <div v-if="field.id === -1">
                                <span v-for="(client, index) in field.value" :key="index">
                                    <b-dropdown v-if="client.source_url || client.company_url || client.partnerships?.length" :text="client.name" boundary="viewport" class="dropdown-details">
                                        <b-dropdown-item v-if="client.company_url" @click="goToPage(client.company_url)" class="dropdown-details__enterprise">
                                            {{client.name}}'s SwitchPitch Profile
                                        </b-dropdown-item>

                                        <b-dropdown-item v-if="client.source_url" @click="goToPage(client.source_url)" class="dropdown-details__globe">
                                            View Information Source
                                        </b-dropdown-item>

                                        <b-dropdown-item
                                            v-if="client.partnerships_count"
                                            @click="partnershipsModal.open({name: client.name, id: client.corporate_id})" class="dropdown-details__partner"
                                        >
                                            {{client.partnerships_count}} {{getNoun('partnership', client.partnerships_count)}}
                                        </b-dropdown-item>
                                    </b-dropdown>

                                    <template v-else>{{client.name}}</template>
                                    <span v-if="index < field.value.length - 1">, </span>
                                </span>
                            </div>

                            <div v-else-if="field.id === -3">
                                <div v-for="(patent, index) in (allPatents ? allPatents : field.value.items)" :key="index">
                                    <div v-if="patent.number" class="mt-2">
                                        <b>Number:</b> {{patent.number}}<br>
                                    </div>
                                    <div v-if="patent.title" class="mt-2">
                                        <b>Title:</b> {{patent.title}}<br>
                                    </div>
                                    <div v-if="patent.assignee" class="mt-2">
                                        <b>Assignee:</b> {{patent.assignee}}<br>
                                    </div>
                                    <div v-if="patent.assignor" class="mt-2">
                                        <b>Assignor:</b> {{patent.assignor}}<br>
                                    </div>

                                    <hr v-if="index <  (allPatents ? allPatents : field.value.items).length - 1">
                                </div>

                                <br>
                                <a
                                    v-if="field.value.has_more && !allPatents"
                                    class="link"
                                    @click="loadAllPatents"
                                >
                                    Load {{field.value.has_more}} More
                                </a>
                            </div>

                            <CustomFieldValue
                                v-else
                                :field="field"
                                :init-value="field.value"
                                :editable="false"
                            />
                        </td>

                        <td>
                            <div class="opinion">
                                <span class="opinion__question">Helpful?</span>

                                <span class="opinion__positive">
                                    <input :disabled="!!customFieldsFeedbacks[field.id]" type="radio" :id="'opinion–' + field.id + '-yes'" value="yes"
                                           @click="() => setCustomFieldFeedback(field.id, 'yes')" v-model="customFieldsFeedbacks[field.id]"
                                    />
                                    <label :for="'opinion–' + field.id + '-yes'"></label>
                                </span>

                                <span class="opinion__negative">
                                    <input :disabled="!!customFieldsFeedbacks[field.id]" type="radio" :id="'opinion–' + field.id + '-no'" value="no"
                                           @click="() => setCustomFieldFeedback(field.id, 'no')" v-model="customFieldsFeedbacks[field.id]"
                                    />
                                    <label :for="'opinion–' + field.id + '-no'"></label>
                                </span>
                            </div>
                        </td>
                    </tr>
                    </tbody>
                </template>
            </table>
        </div>

        <StartupPartnershipsModal
            ref="partnershipsModal"
            :startup-id="companyId"
            :startup-name="companyName"
        />
    </div>
</template>

<style scoped>

</style>
